/**
 * slicer-plugin — the `/slicer` slicer-panel surface as a studio-sibling
 * `ComposableGenomePlugin` (#513b, SLICE 2).
 *
 * Mirrors `cadPlugin`: one `component` editor (`slicer`) routed at `/slicer`,
 * lowering onto `component`/`route` only (NO new EntityKind — gate 4 stays green,
 * the route count derives). The editor is `React.lazy` so the whole slicer
 * surface — `gcode-preview` + three (a dup-three bundled by gcode-preview) + the
 * genome-gen react/runtime stack the embedded settings form pulls — stays in a
 * split chunk OUT of the app entry (gate 8 — the entry gz is unchanged).
 *
 * The GENERATED slice-settings CRUD cluster (the `slicer-profile` DomainModel) is
 * composed SEPARATELY in `genome.ts` via `studioAppPlugin(slicerProfileModel, …)`
 * (its own `/slicer-settings` route); this plugin owns the bespoke side-by-side
 * `/slicer` surface that embeds both.
 *
 * @module @ubiclouds/v5-studio/slicer/slicer-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The slicer-panel surface plugin id. */
export const SLICER_PLUGIN_ID = 'v5-studio-slicer';

/** The split-chunk slicer surface (gcode-preview + three stay out of the entry). */
const LazySlicerSurface = lazy(() => import('./SlicerSurface.js'));

/**
 * The slicer plugin — the `/slicer` route → the lazy slicer surface (the
 * gcode-preview panel + the generated settings form). Composed as a SIBLING of
 * `cadPlugin` in `studioPlugins`.
 */
export const slicerPlugin: ComposableGenomePlugin = definePlugin(SLICER_PLUGIN_ID)
  .name('Slicer')
  .trustTier('core')
  .editor('slicer', LazySlicerSurface)
  .route('/slicer', { routesTo: 'component/slicer', title: 'Slicer' })
  .build();
